// Rollout flags for features that were shipped behind a per-browser localStorage
// opt-in and are now ON by default.
//
// Resolution order for every flag:
//
//   localStorage 'true'/'1'   -> enabled  (wins over the env kill switch)
//   localStorage 'false'/'0'  -> disabled (per-browser opt-out)
//   localStorage unset/other  -> enabled, unless the build's DISABLE env var is set
//
// The env vars are a global kill switch baked in at build time by CRA, so
// flipping one requires a frontend rebuild + redeploy — it is a rollback lever,
// not a live toggle. `process.env.REACT_APP_*` must be referenced as a literal
// member expression here: CRA inlines those statically, so a dynamic
// `process.env[name]` lookup would always be undefined in a production build.
//
//   REACT_APP_DISABLE_DASHBOARD_LITE_REPORTS=true
//   REACT_APP_DISABLE_ALL_TASKS_V2_FILTER=true
//
// Every export is a function, not a constant: localStorage can change while the
// tab is open, and the previous implementations read it per call.

const isEnvDisabled = (rawValue) => {
  if (typeof rawValue !== 'string') return false;
  const value = rawValue.trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'yes';
};

// Returns true/false for an explicit opt-in/opt-out, or null when the browser
// has no opinion. Wrapped in try/catch because localStorage access throws in
// some privacy modes / sandboxed iframes; there we fall back to the env default.
const readLocalOverride = (storageKey) => {
  let raw;
  try {
    raw = localStorage.getItem(storageKey);
  } catch (_) {
    return null;
  }
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return null;
};

const resolveFlag = (storageKey, disableEnvValue) => {
  const override = readLocalOverride(storageKey);
  if (override !== null) return override;
  return !isEnvDisabled(disableEnvValue);
};

// Lite report responses: no per-bucket taskIds, drill-down resolved on demand.
// When this is false the client sends no `includeTaskIds` field at all, which
// the server treats as "legacy/full" — so disabling restores byte-identical
// pre-rollout requests.
export const isLiteReportsEnabled = () =>
  resolveFlag(
    'dashboardLiteReports',
    process.env.REACT_APP_DISABLE_DASHBOARD_LITE_REPORTS,
  );

// v2 all-tasks filter (denormalized projectIds access path). When false the
// client keeps hitting the v1 filter endpoint.
export const isAllTasksV2FilterEnabled = () =>
  resolveFlag(
    'allTasksV2FilterEnabled',
    process.env.REACT_APP_DISABLE_ALL_TASKS_V2_FILTER,
  );
